// The machine detail of PRD section 19.8, at prototype depth: name and icon, what goes in and comes
// out per minute, how hard it works, a soft notice when it is short of material or its output is
// full, and Girar, Mover and Quitar. A card floating beside the machine, as in the design system's kit.
import type { Rect } from '../render/camera-rig';
import type { MachineDef } from '../sim/data';
import type { MachineReport } from '../sim/report';
import { button, progressBar, squircle } from './controls';
import { h, icon } from './dom';
import { percent, perMinute } from './figures';
import { ICONS } from './icons';
import { itemIcon } from './item-icon';

export interface MachineCardHandlers {
  readonly onRotate: () => void;
  readonly onMove: () => void;
  readonly onRemove: () => void;
  readonly onClose: () => void;
}

/** The soft notice for a machine's state, describing the effect, never alarming (design system). */
export function machineNotice(m: MachineReport, def: MachineDef, itemName: (id: string) => string): { text: string; tone: 'amber' | 'info' } | undefined {
  const pace = percent(m.work);
  switch (m.status) {
    case 'starved': {
      const short = [...def.recipe.in.keys()].map((id) => itemName(id).toLowerCase());
      // «Le falta mineral», «le faltan placas»: the verb agrees with the item's name.
      const verb = short.length > 1 || /s$/.test(short[0]?.split(' ')[0] ?? '') ? 'faltan' : 'falta';
      return { text: m.work > 0 ? `Le ${verb} ${short.join(' y ')}: va al ${pace}.` : `Le ${verb} ${short.join(' y ')}: está parada.`, tone: 'amber' };
    }
    case 'blocked':
      return { text: m.work > 0 ? `Su salida está llena: va al ${pace}.` : 'Su salida está llena: está parada.', tone: 'amber' };
    case 'disconnected':
      return { text: 'Lo que hace no llega a ninguna salida: únela con cintas.', tone: 'amber' };
    case 'bottleneck':
      return { text: 'Va al máximo y le llega más de lo que puede usar.', tone: 'info' };
    case 'working':
      return undefined;
  }
}

export class MachineCard {
  readonly el: HTMLElement;
  private readonly title = h('span', { class: 'panel__title-text' });
  private readonly subtitle = h('small', {});
  private readonly frame = h('span', { class: 'machine-card__icon' });
  private readonly rows = h('div', { class: 'machine-card__rows' });
  private readonly work = progressBar();
  private readonly notice = h('div', { class: 'soft-notice', hidden: true });
  private machineId: string | undefined;

  constructor(handlers: MachineCardHandlers) {
    const close = h('button', { type: 'button', class: 'icon-button', 'aria-label': 'Cerrar' }, icon(ICONS.close));
    close.addEventListener('click', handlers.onClose);
    this.el = h(
      'section',
      { class: 'panel machine-card', role: 'dialog', 'aria-label': 'Detalle de máquina', hidden: true },
      h('header', { class: 'panel__header' }, this.frame, h('div', { class: 'panel__title' }, this.title, this.subtitle), close),
      h('div', { class: 'panel__body' }, this.rows, h('div', { class: 'machine-card__work' }, this.work.el), this.notice),
      h(
        'footer',
        { class: 'panel__footer' },
        button({ label: 'Girar', icon: ICONS.arrowsClockwise, variant: 'secondary', size: 'sm', onClick: handlers.onRotate }),
        button({ label: 'Mover', icon: ICONS.arrowsOutCardinal, variant: 'secondary', size: 'sm', onClick: handlers.onMove }),
        button({ label: 'Quitar', icon: ICONS.trash, variant: 'ghost', size: 'sm', onClick: handlers.onRemove }),
      ),
    );
  }

  /** The machine shown, if the card is open. */
  get shown(): string | undefined {
    return this.machineId;
  }

  show(m: MachineReport, def: MachineDef, itemName: (id: string) => string): void {
    if (this.machineId !== m.id) {
      this.machineId = m.id;
      const product = [...def.recipe.out.keys()][0];
      this.frame.replaceChildren(squircle(product ? itemIcon(product, 26) : icon(ICONS.cube), { size: 46 }));
      this.title.textContent = m.name;
    }
    this.el.hidden = false;
    this.subtitle.textContent = `${def.recipe.name} · trabajo ${percent(m.work)}`;
    const amount = (list: MachineReport['consumes'], id: string): number => list.find((a) => a.item === id)?.amount ?? 0;
    const row = (id: string, label: string, value: number): HTMLElement =>
      h('div', { class: 'stat-row' }, h('span', { class: 'stat-row__icon' }, itemIcon(id, 24)), h('span', { class: 'stat-row__label' }, label), h('span', { class: 'stat-row__value' }, perMinute(value)));
    this.rows.replaceChildren(
      ...[...def.recipe.in.keys()].map((id) => row(id, `Entra · ${itemName(id)}`, amount(m.consumes, id))),
      ...[...def.recipe.out.keys()].map((id) => row(id, `Sale · ${itemName(id)}`, amount(m.produces, id))),
    );
    this.work.set(m.work, percent(m.work), m.work < 0.7 ? 'amber' : 'light');
    const notice = machineNotice(m, def, itemName);
    this.notice.hidden = !notice;
    if (notice) {
      this.notice.className = `soft-notice soft-notice--${notice.tone}`;
      this.notice.replaceChildren(icon(notice.tone === 'amber' ? ICONS.hourglass : ICONS.info, 'icon soft-notice__icon'), h('span', {}, notice.text));
    }
  }

  hide(): void {
    this.machineId = undefined;
    this.el.hidden = true;
  }

  /**
   * Floats the card where it hides the least: never over the HUD's panels or the machine itself, as
   * little as it can over the doors' figures and the rest of the tile, and otherwise near the machine.
   */
  place(machine: Rect, width: number, height: number, avoid: { readonly panels: readonly Rect[]; readonly figures: readonly Rect[]; readonly tile: Rect }): void {
    const w = this.el.offsetWidth || 340;
    const hgt = this.el.offsetHeight || 340;
    const edge = 18;
    const lefts = [edge, (width - w) / 2, width - edge - w, machine.right + 24, machine.left - 24 - w];
    const tops: number[] = [];
    for (let y = edge; y <= height - edge - hgt; y += 24) tops.push(y);
    tops.push(Math.max(edge, height - edge - hgt));
    const area = (a: Rect, l: number, t: number): number => Math.max(0, Math.min(l + w, a.right) - Math.max(l, a.left)) * Math.max(0, Math.min(t + hgt, a.bottom) - Math.max(t, a.top));
    const mx = (machine.left + machine.right) / 2;
    const my = (machine.top + machine.bottom) / 2;
    let best = { left: edge, top: edge, score: Infinity };
    for (const left of lefts) {
      if (left < edge || left + w > width - edge) continue;
      for (const top of tops) {
        let score = 10 * area(machine, left, top) + 0.3 * area(avoid.tile, left, top);
        for (const p of avoid.panels) score += 10 * area(p, left, top);
        for (const f of avoid.figures) score += 3 * area(f, left, top);
        // Among equals, the nearest to the machine.
        score += Math.hypot(left + w / 2 - mx, top + hgt / 2 - my);
        if (score < best.score) best = { left, top, score };
      }
    }
    this.el.style.transform = `translate(${Math.round(best.left)}px, ${Math.round(best.top)}px)`;
  }
}
